import React, { useMemo, useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Container } from 'react-bootstrap';
import { Formik } from 'formik';
import { IoArrowBack } from 'react-icons/io5';
import { useHistory, useParams } from 'react-router-dom';

import { Button, PageHeader, Tabs } from '_components/_core';
import { LoadingIcon } from '_components/_shared';

import { CustomFields, EventDetails } from './components';
import { FormFooter } from './styles';
import { validationSchema } from './utilities';

const EventForm = ({ onToggleForm, onCreateEvent, onUpdateEvent, onFetchEvent }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [newEvent, setNewEvent] = useState({});
  const [activeTab, setActiveTab] = useState('details');

  const { id: event_id } = useParams();

  const history = useHistory();

  useEffect(() => {
    if (!event_id) {
      return;
    }

    setIsLoading(true);

    onFetchEvent(event_id, found_event => {
      setNewEvent(found_event);

      setIsLoading(false);
    });
  }, [event_id, onFetchEvent]);

  const initialValues = useMemo(() => {
    if (newEvent) {
      return {
        name: newEvent.name || '',
        address: newEvent.address || '',
        capacity: newEvent.capacity || 100,
        start_date: newEvent.start_date || '',
        end_date: newEvent.end_date || '',
        waiting_list_enabled: newEvent.waiting_list_enabled || false,
        custom_fields: newEvent.custom_fields || [],
        form_layout: newEvent.form_layout || [],
      };
    }

    return {
      name: '',
      address: '',
      capacity: 100,
      start_date: '',
      end_date: '',
      waiting_list_enabled: false,
      custom_fields: [],
      form_layout: [],
    };
  }, [newEvent]);

  const handleSubmit = useCallback(
    async (values, { setSubmitting, resetForm }) => {
      try {
        setIsLoading(true);

        const successCallback = () => {
          resetForm();
          onToggleForm();

          history.push('/eventos');
        };

        const errorCallback = () => {
          setIsLoading(false);
          setSubmitting(false);
        };

        if (newEvent?._id) {
          onUpdateEvent(newEvent._id, values, successCallback, errorCallback);
        } else {
          onCreateEvent(values, successCallback, errorCallback);
        }
      } catch (error) {
        console.error('Failed to save event:', error);

        setIsLoading(false);
        setSubmitting(false);
      }
    },
    [newEvent, onCreateEvent, onUpdateEvent, onToggleForm, history],
  );

  const renderFooter = useCallback(
    (handleSubmit, isValid) => (
      <FormFooter>
        <Button variant="outline-secondary" onClick={onToggleForm} className="flex-fill">
          Cancelar
        </Button>
        <Button
          variant="primary"
          type="submit"
          onClick={handleSubmit}
          disabled={!isValid || isLoading}
          isLoading={isLoading}
          className="flex-fill"
        >
          {newEvent?._id ? 'Atualizar Evento' : 'Criar Evento'}
        </Button>
      </FormFooter>
    ),
    [newEvent, isLoading, onToggleForm],
  );

  const getTabs = useCallback(
    ({ setFieldValue, values, errors, touched, handleChange, handleBlur }) => {
      const props = {
        values,
        setFieldValue,
        errors,
        touched,
        handleChange,
        handleBlur,
      };

      return [
        {
          id: 'details',
          title: 'Detalhes',
          content: activeTab === 'details' && (
            <>
              <EventDetails {...props} />
            </>
          ),
        },
        {
          id: 'customFields',
          title: 'Formulário de Inscrição',
          content: activeTab === 'customFields' && <CustomFields {...props} />,
        },
      ];
    },
    [activeTab],
  );

  return (
    <Container className="content-wrapper">
      <Button
        variant="link"
        className="m-0 p-0 d-flex align-items-center"
        onClick={() => history.goBack()}
      >
        <IoArrowBack className="mr-2" /> Voltar
      </Button>
      <PageHeader
        title={newEvent?._id ? 'Editar Evento' : 'Criar Evento'}
        description={
          <small>Preencha os detalhes do evento e formulário de inscrição</small>
        }
        className="ml-3"
      />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {formikProps => (
          <>
            {isLoading && (
              <div className="h-100 d-flex justify-content-center">
                <LoadingIcon text="Aguarde ..." />
              </div>
            )}
            {!isLoading && (
              <>
                <Tabs
                  variant="pills"
                  activeTab={activeTab}
                  onTabChange={tab => {
                    setActiveTab(tab);
                  }}
                  tabs={getTabs(formikProps)}
                />
                {renderFooter(formikProps.handleSubmit, formikProps.isValid)}
              </>
            )}
          </>
        )}
      </Formik>
    </Container>
  );
};

EventForm.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  event: PropTypes.object,
  onToggleForm: PropTypes.func.isRequired,
  onCreateEvent: PropTypes.func.isRequired,
  onUpdateEvent: PropTypes.func.isRequired,
};

EventForm.defaultProps = {
  event: null,
};

export default EventForm;
